import { motion } from 'framer-motion';
import { webStorage } from '../config';
import { Link } from 'react-router-dom';
import { exploreObjects } from './frontObjs';

type HomeCard = {
    title: string;
    subtitle: string;
    background: string;
    linkTo: string;
    eyebrow: string;
    external?: boolean;
    accent: "gold" | "cyan" | "lime" | "coral";
};

type StatCard = {
    value: string;
    label: string;
    accent: "gold" | "cyan" | "lime";
};

export const FrontPage = () => {
    const discordUrl = "https://discord.gg/boomer-squad-nft-939820143486320650";

    const getAccent = (index: number): HomeCard["accent"] => {
        if (index % 4 === 0) {
            return "gold";
        }
        if (index % 4 === 1) {
            return "cyan";
        }
        if (index % 4 === 2) {
            return "lime";
        }
        return "coral";
    };

    const featureCards: HomeCard[] = [
        ...exploreObjects.map((item, index) => ({
            ...item,
            eyebrow: index === 0 ? "Mattress Fund" : "Platform",
            accent: getAccent(index),
        })),
    ];

    const stats: StatCard[] = [
        {
            value: "7M+ CRO",
            label: "Given back through Mattress Fund, airdrops, and free mints",
            accent: "lime",
        },
        {
            value: "300K+",
            label: "Transactions across more than 40 smart contracts",
            accent: "cyan",
        },
        {
            value: "10.5M+ CRO",
            label: "Recorded in secondary sales across the collections",
            accent: "gold",
        },
    ];

    const accentStyles: Record<HomeCard["accent"] | StatCard["accent"], string> = {
        gold: "from-[#ffd166]/30 via-[#ffd166]/10 to-transparent border-[#ffd166]/30 text-[#ffd166]",
        cyan: "from-[#59d6ff]/30 via-[#59d6ff]/10 to-transparent border-[#59d6ff]/30 text-[#59d6ff]",
        lime: "from-[#9cff74]/30 via-[#9cff74]/10 to-transparent border-[#9cff74]/30 text-[#9cff74]",
        coral: "from-[#ff8a72]/30 via-[#ff8a72]/10 to-transparent border-[#ff8a72]/30 text-[#ff8a72]",
    };

    const fadeUp = {
        hidden: { opacity: 0, y: 18 },
        visible: { opacity: 1, y: 0 },
    };

    const CardLink = ({ card }: { card: HomeCard }) => {
        const content = (
            <motion.div
                variants={fadeUp}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-[28px] border border-white/10 bg-[#101b29]/75 shadow-boomer transition-colors duration-300 hover:border-white/20"
            >
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,10,16,0)_0%,rgba(6,10,16,0.42)_48%,rgba(6,10,16,0.94)_100%)]" />
                <div
                    className="h-44 w-full bg-cover bg-center transition-transform duration-500 group-hover:scale-[1.03]"
                    style={{ backgroundImage: `url(${webStorage + card.background})` }}
                />
                <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/35 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                    <div
                        className={`mb-3 inline-flex rounded-full border bg-gradient-to-r px-3 py-1 text-[11px] uppercase tracking-[0.24em] ${accentStyles[card.accent]}`}
                    >
                        {card.eyebrow}
                    </div>
                    <div className="font-brand text-[26px] uppercase tracking-[0.08em] text-[#f6efd3]">
                        {card.title}
                    </div>
                    <p className="mt-2 max-w-[28rem] text-sm leading-6 text-[#c7d2de]">
                        {card.subtitle}
                    </p>
                </div>
            </motion.div>
        );

        if (card.external) {
            return (
                <a href={card.linkTo} target="_blank" rel="noreferrer">
                    {content}
                </a>
            );
        }

        return <Link to={card.linkTo}>{content}</Link>;
    };

    return (
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top,rgba(89,214,255,0.12),transparent_30%),radial-gradient(circle_at_20%_20%,rgba(255,209,102,0.12),transparent_24%),linear-gradient(180deg,#07111c_0%,#05080d_52%,#05080d_100%)] text-[#f6efd3]">
            <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:72px_72px]" />
            <div className="absolute inset-x-0 top-0 h-64 bg-[radial-gradient(circle_at_top,rgba(156,255,116,0.16),transparent_55%)]" />

            <motion.div
                initial="hidden"
                animate="visible"
                transition={{ staggerChildren: 0.08 }}
                className="relative mx-auto flex min-h-[calc(100vh-80px)] max-w-7xl flex-col gap-10 px-4 pb-20 pt-10 sm:px-6 sm:pt-12 lg:px-8 lg:pt-24"
            >
                <motion.div
                    variants={fadeUp}
                    className="rounded-[32px] border border-[#ffd166]/25 bg-[#281d08]/70 p-4 shadow-boomer-glow backdrop-blur sm:p-6"
                >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="max-w-4xl">
                            <div className="mb-2 text-[11px] uppercase tracking-[0.28em] text-[#ffd166]">
                                Arcade Sunset Update
                            </div>
                            <div className="font-brand text-2xl uppercase tracking-[0.08em] text-[#fff2cc] sm:text-3xl">
                                Important Notice
                            </div>
                            <p className="mt-3 text-sm leading-7 text-[#f6efd3]/84 sm:text-base">
                                Boomer Arcade is sunsetting. SCRATCH refunds have been completed, and staked Zoomers will be distributed soon. If you have any questions or need help, please reach out to us on Discord.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <a
                                href={discordUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center justify-center rounded-full border border-[#ffd166]/35 bg-[#ffd166]/10 px-5 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#fff2cc] transition hover:bg-[#ffd166]/18"
                            >
                                Contact On Discord
                            </a>
                        </div>
                    </div>
                </motion.div>

                <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
                    <motion.section variants={fadeUp} className="max-w-3xl">
                        <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-[#59d6ff]">
                            Boomer Squad Hub
                        </div>
                        <h1 className="font-brand text-5xl uppercase tracking-[0.08em] text-[#fff2cc] sm:text-6xl lg:text-7xl">
                            Everything Live
                            <span className="block text-[#59d6ff]">In One Place</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-8 text-[#c7d2de] sm:text-lg">
                            Access your Boomers and Zoomers, open Boom Rooms, check Mattress Fund access, and jump into the core Boomer Squad pages from one front page.
                        </p>
                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <Link
                                to="/boomrooms"
                                className="inline-flex items-center justify-center rounded-full bg-[#59d6ff] px-6 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#07111c] transition hover:bg-[#7fe0ff]"
                            >
                                Open Boom Rooms
                            </Link>
                            <Link
                                to="/myboomers"
                                className="inline-flex items-center justify-center rounded-full border border-[#9cff74]/35 bg-[#9cff74]/10 px-6 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#dfffd2] transition hover:bg-[#9cff74]/18"
                            >
                                Mattress Fund Access
                            </Link>
                        </div>
                    </motion.section>

                    <motion.aside
                        variants={fadeUp}
                        className="rounded-[32px] border border-white/10 bg-[#101b29]/75 p-6 shadow-boomer backdrop-blur sm:p-8"
                    >
                        <div className="mb-6 flex items-center gap-4">
                            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
                                <img
                                    src={webStorage + "/navbar/boomersquadlogo2.png"}
                                    alt="Boomer Squad"
                                    className="h-11 w-11 object-contain"
                                />
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.28em] text-[#ffd166]">
                                    Available Now
                                </div>
                                <div className="font-brand text-2xl uppercase tracking-[0.08em] text-[#fff2cc]">
                                    Boomer Squad Destinations
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4 text-sm leading-7 text-[#c7d2de]">
                            <p>
                                My Boomers gives holders access to their collection dashboard and Mattress Fund actions, while My Zoomers keeps collection management close at hand.
                            </p>
                            <p>
                                Boom Rooms and Comics round out the live Boomer Squad experience with room building and story content.
                            </p>
                        </div>
                        <div className="mt-8 grid gap-3 sm:grid-cols-2">
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                <div className="text-[11px] uppercase tracking-[0.24em] text-[#59d6ff]">
                                    Holders
                                </div>
                                <div className="mt-2 font-brand text-xl uppercase tracking-[0.08em] text-[#f6efd3]">
                                    My Boomers
                                </div>
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                <div className="text-[11px] uppercase tracking-[0.24em] text-[#9cff74]">
                                    Explore
                                </div>
                                <div className="mt-2 font-brand text-xl uppercase tracking-[0.08em] text-[#f6efd3]">
                                    Boom Rooms
                                </div>
                            </div>
                        </div>
                    </motion.aside>
                </div>

                <motion.section variants={fadeUp}>
                    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.28em] text-[#59d6ff]">
                                Explore
                            </div>
                            <h2 className="mt-2 font-brand text-3xl uppercase tracking-[0.08em] text-[#fff2cc] sm:text-4xl">
                                Core Destinations
                            </h2>
                        </div>
                        <p className="max-w-xl text-sm leading-7 text-[#aebdca]">
                            The current Boomer Squad destinations and collection pages in one place.
                        </p>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                        {featureCards.map((card) => (
                            <CardLink key={`${card.title}-${card.linkTo}`} card={card} />
                        ))}
                    </div>
                </motion.section>

                <motion.section variants={fadeUp}>
                    <div className="mb-6">
                        <div className="text-[11px] uppercase tracking-[0.28em] text-[#9cff74]">
                            Track Record
                        </div>
                        <h2 className="mt-2 font-brand text-3xl uppercase tracking-[0.08em] text-[#fff2cc] sm:text-4xl">
                            What Boomer Squad Built
                        </h2>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                        {stats.map((stat) => (
                            <motion.div
                                key={stat.value}
                                variants={fadeUp}
                                className="rounded-[28px] border border-white/10 bg-[#101b29]/65 p-6 shadow-boomer backdrop-blur"
                            >
                                <div
                                    className={`mb-3 inline-flex rounded-full border bg-gradient-to-r px-3 py-1 text-[11px] uppercase tracking-[0.24em] ${accentStyles[stat.accent]}`}
                                >
                                    Since Launch
                                </div>
                                <div className="font-brand text-4xl uppercase tracking-[0.08em] text-[#fff2cc]">
                                    {stat.value}
                                </div>
                                <p className="mt-3 text-sm leading-7 text-[#c7d2de]">
                                    {stat.label}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>
            </motion.div>
        </div>
    );
};
